import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchNewsByDateRange, NewsListItems, sortNewsArticles } from "./NewsAPIUtil";
import { TranslationType } from 'src/api/translationsApi';
import { MainStoryVideo, SecondaryStoryVideo } from './RenderStoryVideo';
import { processFootnotes, getTranslatedType, getTranslatedMetaInfo } from "./NewsListHelper";
import ArchiveSection from './ArchiveSection';
import useResponsiveArticles from './hooks/useResponsiveArticles';
import {PageTitleDescriptionHelmet } from "src/components/seo/NewsPageHelmets";
import { localizeXImageUrl } from "./NewsDetailsHelper";


interface NewsListProps {
    lang: string;
    type?: string;
    subtype?: string;
    language: string;
    t: TranslationType;  // adjust type accordingly
    getUrlLanguagePrefix: (lang: string) => string;
}

// const NewsList: React.FC<NewsListProps> = ({ lang, type: propType = 'trending', subtype: propSubtype }) => {
const NewsList: React.FC<NewsListProps> = ({ lang, type: propType = 'trending', subtype: propSubtype, language, t, getUrlLanguagePrefix }) => {

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryType = queryParams.get('type');
    const querySubtype = queryParams.get('subtype');
    const queryPage = parseInt(queryParams.get('page') || '1', 10);

    const pathParts = location.pathname.split('/').filter(Boolean);
    const startIndex = pathParts[0]?.match(/^(en|es|fr|cn|tw)$/) ? 5 : 0;
    const pathType = pathParts[startIndex];
    const pathSubtype = pathParts[startIndex + 1];

    const finalType = pathType || queryType || propType;
    const finalSubtype = pathSubtype || querySubtype || propSubtype;

    const now = Math.floor(Date.now());
    const oneYearAgo = now - 365 * 24 * 60 * 60 * 1000;

    const minTrendingScore = finalType === 'trending' ? 60 : 10;
    const apiType = finalType === 'trending' ? null : finalType;

    // const { language, getUrlLanguagePrefix, translations: t } = useTranslations();

    const [news, setNews] = useState<NewsListItems[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [embedKey, setEmbedKey] = useState(0);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const navigate = useNavigate();
    const observer = useRef<IntersectionObserver | null>(null);
    const lastPathname = useRef<string>(location.pathname);
    const isLoadingRef = useRef<boolean>(false);
    const lastLoadedParamsRef = useRef<string>('');
    const loadedPagesRef = useRef<Set<number>>(new Set([1]));

    const MAX_PAGES = 12;
    const NUMBER_OF_ARTICLES = useResponsiveArticles();

    const updateUrlWithPage = (newPage: number) => {
        const newParams = new URLSearchParams(location.search);
        newParams.set('page', newPage.toString());
        navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    };

    const readableType = getTranslatedType(finalType, t) || finalType;
    const readableSubtype = finalSubtype ? getTranslatedType(finalSubtype, t) || finalSubtype : null;
    const readableMetaDescription = getTranslatedMetaInfo("metaDescription", t);
    const readableMetaKeywords = getTranslatedMetaInfo("metaKeywords", t);




    const loadMoreNews = useCallback(async () => {
        if (loadingMore || !hasMore || currentPage >= MAX_PAGES) return;

        const nextPage = currentPage + 1;
        if (loadedPagesRef.current.has(nextPage)) return;

        const loadMoreKey = `${language}:${finalType}:${finalSubtype || 'all'}:page${nextPage}`;
        if (isLoadingRef.current && lastLoadedParamsRef.current === loadMoreKey) return;

        setLoadingMore(true);
        setCurrentPage(nextPage);
        updateUrlWithPage(nextPage);

        isLoadingRef.current = true;
        lastLoadedParamsRef.current = loadMoreKey;

        try {
            const apiParams = {
                startTime: oneYearAgo,
                endTime: now,
                page: nextPage - 1,
                size: NUMBER_OF_ARTICLES,
                lang: lang,
                type: apiType,
                subtype: finalSubtype,
                minTrendingScore
            };

            const newNews = await fetchNewsByDateRange(apiParams);
            if (!newNews || !Array.isArray(newNews)) {
                setHasMore(false);
                return;
            }

           

            const validNews = sortNewsArticles(newNews.filter(item => item.title && item.title.length >= 5));
            validNews.forEach(item => {
                item.title = item.title.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, '');
            });

            const existingIds = new Set(news.map(item => item.id));
            const uniqueNewNews = validNews.filter(item => !existingIds.has(item.id));

            if (uniqueNewNews.length > 0) {
                setNews(prev => [...prev, ...uniqueNewNews]);
                loadedPagesRef.current.add(nextPage);
                setHasMore(validNews.length > 0 && nextPage < MAX_PAGES);
            } else {
                setHasMore(nextPage < MAX_PAGES);
            }
        } catch (error) {
            setHasMore(false);
        } finally {
            setLoadingMore(false);
            isLoadingRef.current = false;
        }
    }, [language, currentPage, hasMore, loadingMore, finalType, finalSubtype, apiType, minTrendingScore, news]);

    const lastNewsElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loadingMore && currentPage < MAX_PAGES) {
                loadMoreNews();
            }
        }, { root: null, rootMargin: '100px', threshold: 0.1 });

        if (node) observer.current.observe(node);
    }, [loading, loadMoreNews, hasMore, loadingMore, currentPage]);

    useEffect(() => {
        const pathChanged = location.pathname !== lastPathname.current;
        lastPathname.current = location.pathname;

        if (pathChanged || !(initialLoadComplete && loadedPagesRef.current.has(queryPage))) {
            if (pathChanged) {
                setNews([]);
                loadedPagesRef.current = new Set([1]);
                setCurrentPage(1);
                setLoading(true);
                setHasMore(true);
                setInitialLoadComplete(false);
            } else if (initialLoadComplete) {
                setCurrentPage(queryPage);
                return;
            }

            const currentParamsKey = `${language}:${finalType}:${finalSubtype || 'all'}:initial:${location.pathname}`;
            if (isLoadingRef.current && lastLoadedParamsRef.current === currentParamsKey) return;

            isLoadingRef.current = true;
            lastLoadedParamsRef.current = currentParamsKey;

            const initialLoadNews = async () => {
                try {
                    const apiParams = {
                        startTime: oneYearAgo,
                        endTime: now,
                        page: 0,
                        size: NUMBER_OF_ARTICLES,
                        lang: language,
                        type: apiType,
                        subtype: finalSubtype,
                        minTrendingScore
                    };

                    const initialNews = await fetchNewsByDateRange(apiParams);
                    if (!initialNews || !Array.isArray(initialNews)) {
                        setNews([]);
                        setHasMore(false);
                        return;
                    }

                    const validNews = sortNewsArticles(initialNews.filter(item => item.title && item.title.length >= 5));
                    validNews.forEach(item => {
                        item.title = item.title.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, '');
                    });

                    setNews(validNews);
                    setHasMore(initialNews.length === NUMBER_OF_ARTICLES);
                    setInitialLoadComplete(true);

                    if (queryPage > 1 && validNews.length > 0) {
                        for (let i = 2; i <= queryPage; i++) {
                            await new Promise(resolve => setTimeout(resolve, 300));
                            setCurrentPage(i - 1);
                            await loadMoreNews();
                        }
                    }
                } catch (error) {
                    setNews([]);
                    setHasMore(false);
                } finally {
                    setLoading(false);
                    setEmbedKey(prev => prev + 1);
                    isLoadingRef.current = false;
                    setInitialLoadComplete(true);
                }
            };

            initialLoadNews();
        }
    }, [language, finalType, finalSubtype, apiType, minTrendingScore, loadMoreNews, location.pathname, queryPage]);

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://platform.twitter.com/widgets.js";
        script.async = true;
        document.body.appendChild(script);
        return () => { script.remove(); };
    }, [embedKey]);

    if (loading) return <p className="text-center text-gray-600">Loading initial news...</p>;



    if (news.length > 0) {
        news.forEach(item => {
            if (item.img_urls && Array.isArray(item.img_urls)) {
                item.img_urls = item.img_urls.map(localizeXImageUrl);
            }
        });
    }

    const mainStory = news.length > 0 ? news[0] : null;
    const secondaryStories = news.length > 1 ? news.slice(1) : [];

    const handleNewsClick = (id: string) => {
        const prefix = getUrlLanguagePrefix(language);
        navigate(`${prefix}/news/trendingnews/${id}`);
    };

    const pageTitle = readableSubtype
        ? `${readableSubtype} – ${readableType} News – Global24`
        : `${readableType} News – Global24`;

    const metaDescription = readableSubtype
        ? `${readableMetaDescription} ${readableSubtype} ${readableType}`
        : `${readableMetaDescription} ${readableType}`;

    const metaKeywords = readableSubtype
        ? `${readableMetaKeywords} ${readableSubtype} ${readableType}`
        : `${readableMetaKeywords} ${readableType}`;

    return (
        <div className="max-w-7xl mx-auto px-1 min-h-screen relative pb-20">
              

            <PageTitleDescriptionHelmet
                title={pageTitle}
                description={metaDescription}
                keywords={metaKeywords}
            />

            {news.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-500 mb-8">
                        {processFootnotes(t?.navigations?.header?.noNewsAvailable || 'No news available for ' + getTranslatedType(finalType, t))}
                    </p>
                </div>
            ) : (
                <>
                    {mainStory && (


                        <MainStoryVideo
                            embedHtml={mainStory.video_embed_link || ''}
                            keyId={mainStory.id}
                            title={mainStory.title}
                            postTime={mainStory.postTime}
                            img_urls={mainStory.img_urls || []}
                            type={mainStory.type || finalType}
                            subtype={mainStory.subtype || ''}
                            t={t}
                            language={language}
                            getUrlLanguagePrefix={getUrlLanguagePrefix}
                            handleNewsClick={handleNewsClick}
                            navigate={navigate}
                        />

                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                        {secondaryStories.map((article, index) => (
                            <article
                                key={article.id}
                                ref={index === news.length - 2 ? lastNewsElementRef : null}
                                className="bg-white rounded-lg overflow-hidden shadow-lg h-full"
                            >

                                <SecondaryStoryVideo
                                    embedHtml={article.video_embed_link || ''}
                                    keyId={article.id}
                                    img_urls={article.img_urls || []}
                                    handleNewsClick={handleNewsClick}
                                />
                                <div className="p-6">
                                    <h2
                                        onClick={() => handleNewsClick(article.id)}
                                        className="text-xl font-extrabold text-blue-900 cursor-pointer hover:text-blue-950 transition-colors line-clamp-4 mb-3 font-serif tracking-tight leading-tight"
                                    >
                                        {processFootnotes(article.title)}
                                        {/* {article.subtype && (
                                            <span className="block text-lg font-medium text-blue-600 mt-1">
                                                {getTranslatedType(article.subtype, t)}
                                            </span>
                                        )} */}
                                    </h2>
                                    <p className="text-gray-600 mt-2 text-sm">
                                        {new Date(article.postTime * 1000).toLocaleDateString()}
                                        <span className="mx-2">•</span>
                                        {article.type && (
                                            <span
                                                className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    navigate(`${getUrlLanguagePrefix(language)}/news/list/${article.type}`);
                                                }}
                                            >
                                                {getTranslatedType(article.type, t)}
                                            </span>
                                        )}
                                        {article.type && article.subtype && " > "}
                                        {article.subtype && (
                                            <span
                                                className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    navigate(`${getUrlLanguagePrefix(language)}/news/list/${article.type}/${article.subtype}`);
                                                }}
                                            >
                                                {getTranslatedType(article.subtype, t)}
                                            </span>
                                        )}
                                    </p>
                                </div>

                            </article>
                        ))}
                    </div>
                </>
            )}

            <ArchiveSection
                getUrlLanguagePrefix={getUrlLanguagePrefix}
                language={language}
                finalType={finalType}
                finalSubtype={finalSubtype}
                navigate={navigate}
                t={t}
            />
        </div>
    );
};

export default NewsList;
